/**
 * Partners Page
 */

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Handshake, Building2, Users, TrendingUp, CheckCircle, ArrowRight } from "lucide-react";

const partnerTypes = [
  {
    icon: Building2,
    title: "Lender Partners",
    description: "Banks, credit unions, and alternative lenders looking to expand their deal flow.",
    benefits: ["Access to qualified borrowers", "Streamlined application process", "Dedicated partnership manager"],
  },
  {
    icon: Users,
    title: "Referral Partners",
    description: "CPAs, attorneys, consultants, and business coaches who work with entrepreneurs.",
    benefits: ["Earn referral commissions", "Add value to your clients", "No sales required"],
  },
  {
    icon: TrendingUp,
    title: "Strategic Partners",
    description: "Organizations looking to offer funding solutions to their members or customers.",
    benefits: ["White-label solutions available", "Co-branded marketing", "Revenue share opportunities"],
  },
];

export default function Partners() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="partners-page">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0f2d52] to-[#1a4a7a] pt-32 pb-20">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="section-label">Partner With Us</span>
              <div className="h-px w-8 bg-[#48d480]" />
            </div>
            <h1 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              Grow Together with <span className="text-[#48d480]">Legacy Triangle</span>
            </h1>
            <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              Partner with us to provide your clients with access to the best business funding solutions in the market.
            </p>
            <Link to="/contact">
              <button className="btn-primary flex items-center gap-2 mx-auto text-lg px-8 py-4">
                Become a Partner <ArrowRight size={18} />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="text-center mb-14">
            <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-3xl md:text-4xl font-bold text-[#0f2d52]">
              Partnership Opportunities
            </h2>
            <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 mt-3 max-w-xl mx-auto">
              We offer multiple ways to partner based on your business model and goals.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {partnerTypes.map((type, idx) => {
              const Icon = type.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-xl hover:border-[#48d480] transition-all">
                  <div className="w-14 h-14 bg-[#0f2d52] rounded-lg flex items-center justify-center mb-6">
                    <Icon size={28} className="text-[#48d480]" />
                  </div>
                  <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold text-xl mb-3">{type.title}</h3>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm mb-6">{type.description}</p>
                  <div className="space-y-3">
                    {type.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-[#48d480]" />
                        <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-700 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: "100+", label: "Lending Partners" },
              { value: "$50M+", label: "Funded Through Partners" },
              { value: "500+", label: "Active Partners" },
              { value: "98%", label: "Partner Satisfaction" },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200">
                <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-3xl md:text-4xl font-black text-[#48d480] mb-1">{stat.value}</div>
                <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-6">
                Why Partner with <span className="text-[#48d480]">Legacy Triangle?</span>
              </h2>
              <div className="space-y-4">
                {[
                  "Access to diverse funding products",
                  "Competitive referral compensation",
                  "Dedicated partner support team",
                  "Co-marketing opportunities",
                  "Training and education resources",
                  "Fast and transparent communication",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-[#48d480] flex-shrink-0" />
                    <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#0f2d52] rounded-2xl p-8">
              <Handshake size={48} className="text-[#48d480] mb-6" />
              <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-2xl font-bold text-white mb-4">
                Ready to Partner?
              </h3>
              <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/70 mb-6">
                Fill out our partner inquiry form and a member of our partnerships team will be in touch within 24 hours.
              </p>
              <Link to="/contact">
                <button className="btn-primary w-full flex items-center justify-center gap-2">
                  Contact Partnerships <ArrowRight size={16} />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0f2d52]">
        <div className="container text-center max-w-2xl mx-auto">
          <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-3xl md:text-4xl font-black text-white mb-4">
            Let's Grow <span className="text-[#48d480]">Together</span>
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 mb-8">
            Join our growing network of partners and help businesses get the funding they need.
          </p>
          <Link to="/contact">
            <button className="btn-primary flex items-center gap-2 mx-auto">
              Become a Partner <ArrowRight size={16} />
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
